/*
  Everlles sticky add-to-cart bar.

  Shows a fixed bottom bar whenever the product form's own "Add to cart" button
  is out of view. Clicking it does one thing: it clicks the real submit button
  of the real product form. That way Dawn's AJAX add, Easify's required-option
  validation and add-on pricing, and the shipping-insurance add-on all run
  exactly as they do for the in-page button.

  Easify REPLACES the theme button with a clone of its own (same id) once its
  options render, so the button is always looked up by id at the moment it is
  needed and never cached.

  It also hides the Essential Cart Drawer sticky bar, which bypassed all of the
  above (it posted the bare variant id with its own request).
*/
if (!customElements.get('sticky-atc')) {
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.priceEl = document.getElementById(this.dataset.priceId);
      this.button = this.querySelector('.sticky-atc__button');
      this.price = this.querySelector('.sticky-atc__price');
      const initial = this.submitButton;
      if (!initial || !this.button) return;

      // The stable container around the (replaceable) button.
      this.container = initial.closest('.product-form') || initial.closest('form') || initial.parentElement;

      this.button.addEventListener('click', () => this.onClick());
      this.hidden = false;

      // Visibility: mirror the in-page button. A plain scroll/resize check
      // (throttled) is used instead of IntersectionObserver so it also behaves
      // in background/preview tabs.
      this.onScroll = () => {
        if (this.scrollTimer) return;
        this.scrollTimer = setTimeout(() => {
          this.scrollTimer = null;
          this.updateVisibility();
        }, 80);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);

      // Keep price + label + disabled state in sync with Dawn's variant changes
      // and with Easify swapping the button.
      const resync = () => this.sync();
      if (this.priceEl) {
        this.priceObserver = new MutationObserver(resync);
        this.priceObserver.observe(this.priceEl, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
        });
      }
      this.containerObserver = new MutationObserver(resync);
      this.containerObserver.observe(this.container, { attributes: true, childList: true, subtree: true });
      this.sync();
      this.updateVisibility();

      hideEssentialStickyBar();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.priceObserver && this.priceObserver.disconnect();
      this.containerObserver && this.containerObserver.disconnect();
    }

    /** Always the live button: Easify may have replaced the original node. */
    get submitButton() {
      return document.getElementById(this.dataset.submitId);
    }

    /** What we watch for visibility: the button row, falling back to the button. */
    get watched() {
      const btn = this.submitButton;
      return (btn && (btn.closest('.product-form__buttons') || btn)) || this.container;
    }

    disabledByState() {
      const btn = this.submitButton;
      return !btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true';
    }

    /** Show the bar only while the real button is fully out of the viewport. */
    updateVisibility() {
      const watched = this.watched;
      if (!watched) return;
      const rect = watched.getBoundingClientRect();
      const inView = rect.bottom > 0 && rect.top < window.innerHeight;
      this.classList.toggle('sticky-atc--visible', !inView && !this.disabledByState());
    }

    sync() {
      // Price: copy the sale price when on sale, otherwise the regular one.
      if (this.priceEl && this.price) {
        const onSale = this.priceEl.querySelector('.price--on-sale');
        const item = onSale
          ? this.priceEl.querySelector('.price__sale .price-item--sale')
          : this.priceEl.querySelector('.price__regular .price-item--regular');
        // Write only on change: the observers above watch this subtree, so an identical
        // rewrite re-triggers sync() forever and starves Easify's 250ms mutation debounce
        // (its variant-driven conditional fields — e.g. Child 2-6 — then never update).
        const priceText = item ? item.textContent.trim() : '';
        if (priceText && this.price.textContent !== priceText) this.price.textContent = priceText;
      }
      // Text + disabled: mirror the real button (Add to cart / Sold out / Unavailable).
      const btn = this.submitButton;
      if (!btn) return;
      const label = btn.querySelector('span');
      const labelText = label ? label.textContent.trim() : '';
      if (labelText && this.button.textContent !== labelText) this.button.textContent = labelText;
      if (this.button.disabled !== btn.disabled) this.button.disabled = btn.disabled;
      if (this.disabledByState()) this.classList.remove('sticky-atc--visible');
    }

    onClick() {
      const btn = this.submitButton;
      if (!btn || this.disabledByState()) return;
      this.button.classList.add('loading');
      // How Easify wires the theme button: its click handler flips the button
      // to type="button", validates the options (data-tpo-validate = true /
      // false, errors rendered + scrolled to), flips it back to type="submit"
      // and lets the click's default action submit the form. Dawn then adds
      // the product via AJAX with Easify's hidden property inputs, and Easify
      // adds its add-on charges a second later. A programmatic click does not
      // get that default action, so after the click we finish the job
      // ourselves: once validation has passed and the button is a submit
      // button again, submit the form with it as the submitter. If the click
      // already submitted natively (no Easify on the page), nothing more runs.
      const form = btn.form;
      let submitted = false;
      const onSubmit = (event) => {
        if (event.target === form) submitted = true;
      };
      document.addEventListener('submit', onSubmit, true);
      btn.click();

      const started = Date.now();
      const finish = () => {
        const live = this.submitButton || btn;
        const validate = live.dataset.tpoValidate;
        const easify = live.classList.contains('tpo_add-to-cart') || validate !== undefined;
        if (submitted || validate === 'false') return done();
        if (validate === 'true' && live.type === 'submit') {
          done();
          if (live.form && typeof live.form.requestSubmit === 'function') live.form.requestSubmit(live);
          return;
        }
        if (Date.now() - started > 2500) {
          done();
          // No Easify on this button and no native submit happened: submit plainly.
          if (!easify && live.type === 'submit' && live.form) live.form.requestSubmit(live);
          return;
        }
        setTimeout(finish, 100);
      };
      const done = () => document.removeEventListener('submit', onSubmit, true);
      setTimeout(finish, 100);
      setTimeout(() => this.button.classList.remove('loading'), 1500);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /**
   * The Essential app injects its sticky bar late and with hashed class names,
   * so it cannot be targeted from CSS. Find its form and hide the fixed
   * wrapper around it, now and whenever the app re-renders it.
   */
  function hideEssentialStickyBar() {
    const hide = () => {
      document.querySelectorAll('form[action*="essential-sticky-cart"]').forEach((form) => {
        let node = form;
        let fixed = null;
        while (node && node !== document.body) {
          const pos = window.getComputedStyle(node).position;
          if (pos === 'fixed' || pos === 'sticky') fixed = node;
          node = node.parentElement;
        }
        const target = fixed || form;
        // Only write when needed, so our own style change never re-triggers the observer.
        if (target.style.getPropertyValue('display') !== 'none') {
          target.style.setProperty('display', 'none', 'important');
        }
      });
    };
    hide();
    const mo = new MutationObserver(hide);
    mo.observe(document.body, { childList: true, subtree: true });
  }
}
